import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    Input,
    OnInit,
    ViewChild,
    ViewEncapsulation,
} from '@angular/core';
import { LoadingService } from '@fi-shared/fi-loading/loading.service';
import { CropImageService } from '@fi-app/utilities/crop-image/crop-image.service';
import { FileTypeService } from '@fi-shared/fi-shared-services/file-type.service';
import { MediaBankService } from '@fi-app/media-bank/media-bank.service';
import { FileUploadModelWithTakePhoto } from '@fi-app/utilities/file-upload/file-upload.model';
import { DetectDevicesService } from '@fi-shared/fi-shared-services';
import { ICropImageDialogResult, ICropImageSelection } from '@fi-app/utilities/crop-image/crop-image';
import { AppTextService } from '@fi-shared/app-text';
import { FiMediaStorageService } from '@fi-shared/media-bank/fi-media-storage.service';
import { FiImageSize, FileType, ObjectTypes } from '@fi-shared/types';
import { FiProfileIconComponent, FiProfileSizes, IUserProfile } from '@fi-shared/profile-icon/profile-icon.component';
import { FiDialogsService } from '@fi-shared/fi-dialogs';
import { FiFileUpload2Service } from '@fi-shared/fi-insert-media/fi-file-upload2.service';
import { IMediaBankFileReference } from '@fi-shared/media-bank';
import { MediaBankReferenceType } from '@fi-shared/media-bank/media-bank-reference-type';
import { CropImageDialogComponent } from '@fi-app/utilities/crop-image/crop-image-dialog/crop-image-dialog.component';
import { FiInsertMedia2Service } from '@fi-shared/fi-insert-media/fi-insert-media2.service';
import { MatMenuTrigger } from '@angular/material/menu';

export enum ProfileImageAlignment {
    BottomRight,
    BottomCenter,
    TopRight,
}

@Component({
    selector: 'fi-profile-edit',
    templateUrl: './profile-edit.component.html',
    styleUrls: ['./profile-edit.component.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: { class: 'fi-profile-edit' },
    standalone: false,
})
export class ProfileEditComponent implements OnInit {
    @Input() isNewLayout: boolean = false; // todo: remove when same (new) layout will be used in business units edit page
    @Input() profileImageAlignment = ProfileImageAlignment.BottomRight;
    @Input() profileItem: {
        MediaBankFileReference: IMediaBankFileReference;
        OriginalMediaBankFileReference: IMediaBankFileReference;
        Id?: number;
        FullName?: string;
        MediaBankPhysicalFileName: string;
        MediaBankExtension: string;
        isLocked: () => boolean;
    };

    @Input() isSquareProfileImage: boolean;
    @Input() iconClass: string;
    @Input() isNewProfileIcon: boolean;
    @Input() objectType: ObjectTypes;
    @Input() dndOverlayIconsSize: number;
    @Input() isDndOverlayTextShown: boolean;
    FiProfileSizes = FiProfileSizes;
    FileUploadModel: FileUploadModelWithTakePhoto;

    private selection: ICropImageSelection;
    cropImageData: string;
    cropImageDataTransformed: string;
    isProfileImageOverlayShown: boolean = false;
    @ViewChild(FiProfileIconComponent, { static: false }) profileIconComponent: FiProfileIconComponent;
    private editImageMenuTrigger: MatMenuTrigger;

    @ViewChild('editImageMenuTrigger') set menuTrigger(menuTrigger: MatMenuTrigger) {
        if (menuTrigger) {
            this.editImageMenuTrigger = menuTrigger;
        }
    }

    constructor(
        private appTextService: AppTextService,
        private fiLoadingService: LoadingService,
        private cropImageService: CropImageService,
        private fileTypeService: FileTypeService,
        private mediaBankService: MediaBankService,
        private fiDialogsService: FiDialogsService,
        private mediaStorageService: FiMediaStorageService,
        private detectDevices: DetectDevicesService,
        private fileUploadService: FiFileUpload2Service,
        private cdr: ChangeDetectorRef,
        private fiInsertMedia2Service: FiInsertMedia2Service,
    ) {}

    ngOnInit() {
        this.FileUploadModel = new FileUploadModelWithTakePhoto(
            ($files, fromCamera) => this.onFileSelect($files, fromCamera),
            this.detectDevices.isMobilePhone(),
        );
        this.FileUploadModel.accept = '.jpg,.jpeg,.gif,.png';
        this.FileUploadModel.acceptValidate = ($file) => this.validateImage($file);
        this.FileUploadModel.fileTypes = [FileType.Image];

        if (this.objectType) {
            // we should not show 'browse file manager' for request access
            this.FileUploadModel.setBrowseMediaBankDone(this.objectType, (mediaBankFileReferences) => {
                // generate square image from selected file from media bank
                this.generateSquareImage(mediaBankFileReferences[0], false);
            });
        }

        /*const profileUrl = this.getProfileItemImage();
        // fix media bank file references
        // https://chainformation.atlassian.net/browse/FI-1699
        if (profileUrl && !this.MediaBankFile.Id) {
            this.cropImageService.getSquareImage(profileUrl, true).then(result => {
                this.MediaBankFile.Id = null;
                this.MediaBankFile.Url = result.data;
                this.MediaBankFile.PhysicalFileName = this.profileItem.Name + FiMediaStorageService.DefaultMediaBankImageExtension;
                this.OriginalMediaBankFile.Url = result.data;
                this.OriginalMediaBankFile.PhysicalFileName = this.profileItem.Name + FiMediaStorageService.DefaultMediaBankImageExtension;
            });
        }*/
    }

    isProfileImageAbsent(): boolean {
        return !this.getProfileData().MediaBankPhysicalFileName;
    }

    setProfileImageOverlayVisibility(value: boolean) {
        this.isProfileImageOverlayShown = value;
    }

    openEditMenu() {
        this.editImageMenuTrigger.openMenu();
    }

    openAttachmentDialog() {
        this.fiInsertMedia2Service
            .openInsertMediaDialogWithFileSizes(this.FileUploadModel.objectType, {
                multiple: this.FileUploadModel.multiple,
                accept: this.FileUploadModel.accept,
                fileTypes: this.FileUploadModel.fileTypes,
            })
            .onYesButtonClick((result) => {
                this.selection = undefined;
                if (this.FileUploadModel.browseMediaDoneCallback) {
                    this.FileUploadModel.browseMediaDoneCallback(result.mediaBankFileReferences);
                }
            });
    }

    getProfileData(): IUserProfile {
        let mediaBankPhysicalFileName: string;
        let mediaBankExtension: string;
        if (this.profileItem.MediaBankFileReference) {
            mediaBankPhysicalFileName = this.profileItem.MediaBankFileReference.PhysicalFileName;
            mediaBankExtension = this.profileItem.MediaBankFileReference.Extension;
        }
        return {
            Id: this.profileItem.Id,
            MediaBankPhysicalFileName: mediaBankPhysicalFileName,
            MediaBankExtension: mediaBankExtension,
            FullName: this.profileItem.FullName,
        };
    }

    private checkFileSize($file) {
        if ($file.size <= this.fileTypeService.getFileUploadLimit($file.name) * 1024 * 1024) return true;

        this.fiDialogsService.error({
            messageText: this.appTextService.get('media_bank.upload_error_too_large'),
        });
        return false;
    }

    onFileSelect(files: FileList, fromCamera?: boolean) {
        if (files.length > 0 && this.checkFileSize(files[0])) {
            if (!this.validateImage(files[0])) return;

            const file = files[0];
            this.fileUploadService.silentUploadFiles([file]).then((mediaBankFileReferences) => {
                this.generateSquareImage(mediaBankFileReferences[0], fromCamera);
            });
        }
    }

    private generateSquareImage(mediaBankFileReference: IMediaBankFileReference, fromCamera: boolean): Promise<void> {
        const promise = new Promise<void>((resolve, reject) => {
            const imageSrc = this.mediaStorageService.getMediaFileUrl(mediaBankFileReference, {
                size: FiImageSize.Medium,
            });
            this.cropImageService.getSquareImage(imageSrc).then(
                (squareImageResult) => {
                    if (squareImageResult) {
                        this.cropImageData = squareImageResult.data;
                        resolve();
                    } else {
                        reject();
                    }
                },
                () => {
                    reject();
                },
            );
        });
        promise.then(
            () => {
                this.profileItem.OriginalMediaBankFileReference = mediaBankFileReference;
                this.profileItem.OriginalMediaBankFileReference.ReferenceType = MediaBankReferenceType.Original;
                this.profileItem.MediaBankFileReference = mediaBankFileReference;
                this.profileItem.MediaBankFileReference.ReferenceType = MediaBankReferenceType.Default;
                if (this.profileIconComponent) {
                    this.profileIconComponent.detectChanges();
                }
                this.cdr.markForCheck();

                if (fromCamera) this.openCropImage();
            },
            () => {
                this.fiDialogsService.error();
            },
        );
        return this.fiLoadingService.trackPromise(promise);
    }

    canCropImage(): boolean {
        return !!this.profileItem.OriginalMediaBankFileReference;
    }

    openCropImage() {
        const imageSrc = this.mediaStorageService.getMediaFileUrl(this.profileItem.MediaBankFileReference, {
            size: FiImageSize.Medium,
        });
        const cropFileUrl = this.mediaStorageService.getMediaFileUrl(this.profileItem.OriginalMediaBankFileReference, {
            size: FiImageSize.Medium,
        });
        this.getDataURL(imageSrc, (imageData) => {
            this.fiDialogsService
                .custom({
                    yesButtonText: this.appTextService.get('common.crop'),
                    headerText: this.appTextService.get('common.crop_image'),
                    contentComponentRef: CropImageDialogComponent,
                    contentComponentData: {
                        selection: this.selection,
                        imgUrl: cropFileUrl,
                        imageData: imageData, // pass image data for parsing EXIF
                    },
                })
                .onYesButtonClick((cropResult: ICropImageDialogResult) => {
                    this.selection = cropResult.selection;
                    this.cropImageData = cropResult.cropImageData;
                    this.cdr.markForCheck();
                });
        });
    }

    private getDataURL(url: string, callback: (result: string) => void) {
        const xhr = new XMLHttpRequest();
        xhr.open('get', url);
        xhr.responseType = 'blob';
        xhr.onload = () => {
            const fr = new FileReader();
            fr.onload = function () {
                callback(this.result as string);
            };

            fr.readAsDataURL(xhr.response);
        };
        xhr.send();
    }

    profileItemOnSave(): Promise<void> {
        const promise = new Promise<void>((resolve, reject) => {
            if (!this.cropImageData) resolve();

            // if profile has been changed we should upload it to backend before save
            const cropImageArr: any = this.cropImageService.base64toUint8Array(this.cropImageData);
            const file: any = new Blob([cropImageArr], { type: 'image/jpeg' });
            file.name =
                this.profileItem.OriginalMediaBankFileReference.Name +
                this.profileItem.OriginalMediaBankFileReference.Extension;
            this.fileUploadService.silentUploadFiles([file]).then((mediaBankFileReferences) => {
                this.profileItem.MediaBankFileReference = mediaBankFileReferences[0];
                this.profileItem.MediaBankPhysicalFileName = mediaBankFileReferences[0].PhysicalFileName;
                this.profileItem.MediaBankExtension = mediaBankFileReferences[0].Extension;
                resolve();
            });
        });
        return promise;
    }

    deleteMediaBankFile() {
        this.profileItem.MediaBankPhysicalFileName = null;
        this.profileItem.MediaBankExtension = null;
        this.profileItem.MediaBankFileReference = null;
        this.profileItem.OriginalMediaBankFileReference = null;
        this.cropImageData = null;
        this.selection = undefined;
    }

    getProfileItemImage() {
        if (this.cropImageData) return this.cropImageData;

        if (this.profileItem.MediaBankFileReference) {
            return this.mediaStorageService.getMediaFileUrl(this.profileItem.MediaBankFileReference, {
                size: FiImageSize.Medium,
            });
        }
        /*if (!this.MediaBankFile.Id && this.MediaBankFile.Url)
            return this.MediaBankFile.Url;

        if (isDefined(this.profileItem.MediaBankPhysicalFileName) && this.profileItem.MediaBankPhysicalFileName.length)
            return this.mediaStorageService.getMediaFileUrl({
                PhysicalFileName: this.profileItem.MediaBankPhysicalFileName,
                Extension: this.profileItem.MediaBankExtension ?? FiMediaStorageService.DefaultMediaBankImageExtension,
            }, { size: FiImageSize.Medium})

        return false;*/
    }

    private validateImage($file) {
        // Image is validating while dragging over drag area (mousemove)
        if ($file.type.startsWith('image/')) return true;

        this.fiDialogsService.error({
            messageText: this.appTextService.get('media_bank.upload_validation_error'),
        });
        return false;
    }

    isNewProfileIconShown() {
        return /*this.profileItem && this.profileItem.Id && */ this.isNewProfileIcon && !this.cropImageData;
    }

    getEditProfileImageButtonClasses(): { [key: string]: boolean } {
        return {
            BrandedBackgroundColor: this.profileImageAlignment === ProfileImageAlignment.BottomRight,
            BrandedColor: this.profileImageAlignment !== ProfileImageAlignment.BottomRight,
            centered: this.profileImageAlignment === ProfileImageAlignment.BottomCenter,
            'top-right': this.profileImageAlignment === ProfileImageAlignment.TopRight,
        };
    }
}
